/**
 * ComponentGroup — "choose N of these ITEMS", the composite/meal-deal primitive.
 *
 * 🔴 WHY THIS IS NOT A MODIFIER GROUP. A modifier is not an order line. It has
 * no `courseId`, no `prepStationIds`, no `salesCategoryId` and no recipe, and
 * it hangs inside `OrderModifierGroup` on its parent. Every coursing mechanism
 * keys on the LINE — `Order.courseStates` is keyed by `courseId`,
 * `kitchenTicketEngine` groups printed sections by `courseId`, and
 * `deriveFireJobId` is `(ticketId, printerId, courseId)`. So a "Lunch Special:
 * 1 snack + 1 entrée" built from modifier groups routes both picks to ONE
 * station, fires them as ONE course, and posts the whole ticket to ONE sales
 * category. Building it from real items is the only shape that works.
 *
 * A choice therefore points at a REAL `itemId`. At ring-up the composite
 * expands into a parent line carrying the price and child lines that are
 * ordinary order items — see `compositeItemResolver.ts`.
 *
 * ─── THE menuGroupId ON A CHOICE IS LOAD-BEARING ───────────────────────────
 *
 * A composite child NAVIGATES NOTHING. The guest picked "Lunch Special", so
 * there is no ROOT→LEAF chain to walk, and the parent's chain is the WRONG one
 * — it would resolve the Lunch Specials group's course and station for a dish
 * whose kitchen behaviour belongs to its own home group (Appetizers → fryer).
 *
 * This is the same trap `docs/ORDERUP_AI_ARCHITECTURE.md` names for
 * `resolveItemPrice` and a conversational guest, in a new place.
 *
 * `menuGroupId` is therefore declared per choice at AUTHORING time rather than
 * derived, because an item can belong to several menu groups (`MenuGroup.items`
 * is `IItemReference[]`, many-to-many) and picking one at runtime would be a
 * coin flip that silently changes which station cooks the dish.
 *
 * Downstream, `courseResolver.resolveCourseId` and
 * `prepStationResolver.resolvePrepStationIds` both take exactly
 * `(menuGroupId, itemOwnValue, groups)` — so a child that carries its own group
 * resolves correctly through the EXISTING resolvers with no new code. Their
 * precedences deliberately differ (course = highest branch wins, station =
 * nearest ancestor); nothing here unifies them.
 */

/** One selectable item inside a component group. */
export interface IComponentChoice {
  /** The REAL menu item this choice rings up. */
  readonly itemId: number;
  /**
   * The menu group this choice's line claims — its HOME group, not the
   * composite's. Drives course and prep-station resolution for the child line.
   * See the file header: this is declared, never derived.
   */
  readonly menuGroupId?: number;
  /**
   * Upcharge for picking this option, in currency units. Absent/0 means the
   * choice is included in the composite's price. Never negative — a discount
   * belongs on the composite, not on one arm of it.
   */
  readonly priceDelta?: number;
  /**
   * NO `position` — deliberately.
   *
   * A menu item ALREADY carries its position, in the menu group that owns it:
   * `menuDisplayOrder.ts` is the SSOT and says the group's `items[]` array is
   * the source of truth for membership AND order. A per-choice position here
   * would be a SECOND, competing answer to "where does this item sit", so an
   * operator could reorder the menu and every composite would silently keep
   * its own stale order — with nothing on screen looking wrong.
   *
   * The order is the authored `choices` array, which the management editor
   * reorders in place. The removed field was never written by anything: it
   * defaulted to 0 on every choice ever saved, so the POS sort that read it
   * ranked them all equal and returned the input order regardless. Two ways of
   * saying the same thing, one of which could silently drift.
   *
   * Pinned by `__tests__/componentGroupParity.test.ts` and its Dart twin.
   */
  /** Hide without deleting, so an 86'd option keeps its authoring history. */
  readonly isHidden?: boolean;
}

/** A "choose N" prompt on a composite item. */
export interface IComponentGroup {
  /** Stable identity for this group within its parent item. */
  readonly componentGroupId: number;
  /** Customer-facing prompt ("Choose Snack"). */
  readonly name: string;
  /** Minimum selections. */
  readonly minSelection: number;
  /** Maximum selections. */
  readonly maxSelection: number;
  /**
   * NO `position` — same rule as `IComponentChoice`. The authored
   * `componentGroups` array IS the order and the editor reorders it in place.
   * The removed field was never written by anything either.
   */
  /** The selectable items. */
  readonly choices: IComponentChoice[];
}

export class ComponentChoice implements IComponentChoice {
  readonly itemId: number;
  readonly menuGroupId?: number;
  readonly priceDelta?: number;
  readonly isHidden?: boolean;

  constructor(data: IComponentChoice) {
    this.itemId = data.itemId;
    this.menuGroupId = data.menuGroupId;
    this.priceDelta = data.priceDelta;
    this.isHidden = data.isHidden;
  }

  toJSON(): IComponentChoice {
    return {
      itemId: this.itemId,
      ...(this.menuGroupId != null ? { menuGroupId: this.menuGroupId } : {}),
      ...(this.priceDelta != null ? { priceDelta: this.priceDelta } : {}),
      ...(this.isHidden != null ? { isHidden: this.isHidden } : {}),
    };
  }

  static fromJSON(data: Record<string, unknown>): ComponentChoice {
    return new ComponentChoice({
      itemId: data.itemId as number,
      menuGroupId: data.menuGroupId as number | undefined,
      priceDelta: data.priceDelta as number | undefined,
      isHidden: data.isHidden as boolean | undefined,
    });
  }
}

export class ComponentGroup implements IComponentGroup {
  readonly componentGroupId: number;
  readonly name: string;
  readonly minSelection: number;
  readonly maxSelection: number;
  readonly choices: ComponentChoice[];

  constructor(data: IComponentGroup) {
    this.componentGroupId = data.componentGroupId;
    this.name = data.name;
    this.minSelection = data.minSelection;
    this.maxSelection = data.maxSelection;
    this.choices = (data.choices ?? []).map((c) => new ComponentChoice(c));
  }

  toJSON(): IComponentGroup {
    return {
      componentGroupId: this.componentGroupId,
      name: this.name,
      minSelection: this.minSelection,
      maxSelection: this.maxSelection,
      choices: this.choices.map((c) => c.toJSON()),
    };
  }

  static fromJSON(data: Record<string, unknown>): ComponentGroup {
    return new ComponentGroup({
      componentGroupId: data.componentGroupId as number,
      name: data.name as string,
      minSelection: data.minSelection as number,
      maxSelection: data.maxSelection as number,
      choices: ((data.choices as Record<string, unknown>[]) ?? []).map((c) =>
        ComponentChoice.fromJSON(c),
      ),
    });
  }
}

/**
 * Is this item a composite? The ONE place the question is answered, so that
 * "has component groups" and "behaves as a composite" cannot drift apart.
 *
 * An empty `componentGroups: []` is NOT a composite — it is an item whose
 * groups were all removed, and it must ring up as an ordinary line rather than
 * as a parent with nothing under it.
 */
export function isCompositeItem(item: {
  componentGroups?: readonly IComponentGroup[];
}): boolean {
  return (item.componentGroups?.length ?? 0) > 0;
}
